import React, { useState } from 'react';
import { 
  BookOpen,
  Target,
  Clock,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  GraduationCap,
  FileText,
  Layers,
  Award,
  Calendar,
  Users,
  Lightbulb,
  ClipboardList,
  Activity,
  Download,
  Printer,
  Link as LinkIcon
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { Progress } from "@/components/ui/progress";

// Year 13 ICT - Unit Plans Data
const unitPlansData = {
  subject: "Year 13 ICT",
  year: 2025,
  strands: [
    {
      id: "strand1",
      number: 1,
      title: "Personal Computer Systems",
      subtitle: "ICT Trends & Information Systems",
      color: "blue",
      units: [
        {
          id: "1.1",
          title: "Future ICT Trends and Their Key Features",
          duration: "2 weeks",
          sessions: 2,
          overview: "This unit introduces students to emerging ICT trends and their potential impact on education, health, and home life. Students will identify key trends and describe their features in the Vanuatu context.",
          objectives: [
            "Identify at least 3 future ICT trends in education, health, and home",
            "State and explain key features of each identified trend",
            "Understand the relevance of ICT trends to Pacific Island contexts"
          ],
          prerequisites: ["Basic understanding of current ICT applications", "Research skills"],
          keyVocabulary: ["AI", "IoT", "VR/AR", "Telehealth", "Smart home", "5G/6G"],
          teachingStrategies: [
            "Brainstorming and group discussions",
            "Research-based learning with internet resources",
            "Group presentations and peer learning",
            "Connecting to local Vanuatu examples"
          ],
          assessmentPlan: {
            formative: ["Class participation", "Group work observation", "Homework review"],
            summative: [{ title: "ICT Trends Quiz", marks: 15, type: "Quiz" }]
          },
          differentiation: {
            struggling: ["Pre-selected list of trends", "Sentence starters", "Paired work"],
            advanced: ["Research additional trends", "Analyze implications", "Present to class"]
          },
          resources: ["Computers with internet", "Projector", "Research handouts", "Flip chart paper"],
          crossCurricular: ["English (research and presentation)", "Social Studies (impact on society)"],
          homeworkTasks: [
            "Research one additional ICT trend",
            "Interview family member about technology changes"
          ]
        },
        {
          id: "1.2",
          title: "Workflows and Information Systems",
          duration: "6 weeks",
          sessions: 6,
          overview: "Students explore the concept of workflows in organizations and understand how Information Systems support and automate business processes. Focus on Vanuatu-relevant examples like Student Information Systems and Tourism Management Systems.",
          objectives: [
            "Define workflow and identify its key parts",
            "Describe workflow processes in local organizations",
            "Explain the relationship between workflows and IS",
            "Identify IS applications and benefits",
            "Understand IS development lifecycle"
          ],
          prerequisites: ["Unit 1.1 completion", "Basic organizational understanding"],
          keyVocabulary: ["Workflow", "Information System", "SIS", "Database", "SDLC", "Implementation"],
          teachingStrategies: [
            "Case study analysis of local organizations",
            "Hands-on exploration of IS examples",
            "Guest speaker from local organization",
            "Group project work"
          ],
          assessmentPlan: {
            formative: ["Workflow diagrams", "Discussion participation", "Weekly reflections"],
            summative: [{ title: "IS Case Study Project", marks: 25, type: "Project" }]
          },
          differentiation: {
            struggling: ["Pre-filled templates", "Visual workflow diagrams", "Step-by-step guides"],
            advanced: ["Design IS for hypothetical organization", "Critique existing systems"]
          },
          resources: ["Computers", "Case studies", "Local organization examples", "IS documentation"],
          crossCurricular: ["Business Studies (organizational processes)", "Economics (efficiency)"],
          homeworkTasks: [
            "Document a household workflow",
            "Research an IS used in Vanuatu",
            "Interview someone using an IS at work"
          ]
        },
        {
          id: "1.3",
          title: "ICT Infrastructure and Networking",
          duration: "3 weeks",
          sessions: 5,
          overview: "This unit covers ICT infrastructure components and networking concepts relevant to Vanuatu organizations. Students learn about network types, software choices, and implementation considerations for Pacific Island contexts.",
          objectives: [
            "Identify ICT infrastructure hardware components",
            "Understand implementation concerns for local contexts",
            "Compare and contrast network types (LAN, WAN, MAN, Wi-Fi)",
            "Differentiate open-source and proprietary software",
            "Demonstrate software installation and management"
          ],
          prerequisites: ["Units 1.1 and 1.2", "Basic computer literacy"],
          keyVocabulary: ["LAN", "WAN", "MAN", "Wi-Fi", "Open-source", "Proprietary", "Infrastructure"],
          teachingStrategies: [
            "Practical demonstrations of network setups",
            "Software installation workshops",
            "Comparison charts and discussions",
            "Local infrastructure case studies"
          ],
          assessmentPlan: {
            formative: ["Network diagrams", "Software comparison tables", "Practical observations"],
            summative: [{ title: "Strand 1 Term Test", marks: 40, type: "Test" }]
          },
          differentiation: {
            struggling: ["Focus on 2 network types", "Guided installation steps", "Visual guides"],
            advanced: ["Research cloud infrastructure", "Compare enterprise solutions"]
          },
          resources: ["Computers", "Network diagrams", "Software installation media", "Network equipment"],
          crossCurricular: ["Physics (networking concepts)", "Business (cost analysis)"],
          homeworkTasks: [
            "Research local ISP infrastructure",
            "Compare open-source alternatives",
            "Document home network setup"
          ]
        }
      ]
    },
    {
      id: "strand2",
      number: 2,
      title: "Website Development",
      subtitle: "Digital Media & Web Design",
      color: "green",
      units: [
        {
          id: "2.1",
          title: "Digital Media Fundamentals",
          duration: "6 weeks",
          sessions: 7,
          overview: "Students learn about digital media types, creation tools, and modification techniques. Emphasis on practical skills using industry-standard software for audio, image, and video editing.",
          objectives: [
            "Understand the importance of digital media in communication",
            "Identify necessary devices for media creation",
            "Use software tools to create and modify media",
            "Apply media modification techniques",
            "Integrate multiple media types"
          ],
          prerequisites: ["Basic computer skills", "File management knowledge"],
          keyVocabulary: ["Digital media", "Resolution", "Codec", "Compression", "File format", "Rendering"],
          teachingStrategies: [
            "Hands-on software workshops",
            "Project-based learning",
            "Peer review of media creations",
            "Real-world application scenarios"
          ],
          assessmentPlan: {
            formative: ["Software exercises", "Media modifications", "Peer feedback"],
            summative: [{ title: "Digital Media Creation", marks: 30, type: "Practical" }]
          },
          differentiation: {
            struggling: ["Focus on one media type first", "Step-by-step tutorials", "Simplified projects"],
            advanced: ["Complex media integration", "Advanced effects", "Portfolio creation"]
          },
          resources: ["Computers", "Audacity", "GIMP", "Video editor", "Media files"],
          crossCurricular: ["Art (visual design)", "Music (audio editing)", "Media Studies"],
          homeworkTasks: [
            "Create a personal media project",
            "Document media creation process",
            "Analyze media from local sources"
          ]
        },
        {
          id: "2.2",
          title: "Website Development Fundamentals",
          duration: "5 weeks",
          sessions: 15,
          overview: "Comprehensive introduction to web development including CSS styling, HTML integration, and database-driven websites. Students create functional websites for local Vanuatu contexts.",
          objectives: [
            "Identify web design requirements for organizations",
            "Master CSS syntax and types",
            "Link CSS effectively with HTML",
            "Understand database-driven website concepts",
            "Create and test functional websites"
          ],
          prerequisites: ["Basic HTML knowledge", "Unit 2.1 completion"],
          keyVocabulary: ["CSS", "Selector", "Property", "Value", "Responsive", "Database", "Backend"],
          teachingStrategies: [
            "Code-along sessions",
            "Incremental project building",
            "Website critique and analysis",
            "Real client projects"
          ],
          assessmentPlan: {
            formative: ["Code exercises", "Website checkpoints", "Peer reviews"],
            summative: [
              { title: "Website Development Project", marks: 40, type: "Project" },
              { title: "Strand 2 Term Test", marks: 30, type: "Test" }
            ]
          },
          differentiation: {
            struggling: ["Templates provided", "CSS frameworks", "Pair programming"],
            advanced: ["JavaScript integration", "Advanced CSS features", "CMS exploration"]
          },
          resources: ["Computers", "Text editor/IDE", "Web browsers", "Database tools", "Hosting"],
          crossCurricular: ["Art (design principles)", "Business (client requirements)", "English (content)"],
          homeworkTasks: [
            "Practice CSS styling",
            "Analyze local business websites",
            "Design wireframes for project"
          ]
        }
      ]
    },
    {
      id: "strand3",
      number: 3,
      title: "Programming",
      subtitle: "Problem Solving & Software Development",
      color: "purple",
      units: [
        {
          id: "3.1",
          title: "Problem Solving Fundamentals",
          duration: "2 weeks",
          sessions: 4,
          overview: "Foundation unit establishing problem-solving skills essential for programming. Students learn systematic approaches to analyzing problems and developing solutions.",
          objectives: [
            "Understand the importance of problem-solving in programming",
            "Apply the four-step problem-solving process",
            "Analyze problems systematically",
            "Develop and evaluate solution strategies"
          ],
          prerequisites: ["Logical thinking skills", "Basic mathematics"],
          keyVocabulary: ["Problem-solving", "Algorithm", "Analysis", "Solution", "Testing", "Refinement"],
          teachingStrategies: [
            "Real-world problem scenarios",
            "Group problem-solving activities",
            "Think-pair-share discussions",
            "Step-by-step guided practice"
          ],
          assessmentPlan: {
            formative: ["Problem analysis worksheets", "Group discussions", "Solution presentations"],
            summative: [{ title: "Problem Solving Quiz", marks: 15, type: "Quiz" }]
          },
          differentiation: {
            struggling: ["Guided problem templates", "Simpler scenarios", "Visual aids"],
            advanced: ["Complex multi-step problems", "Time constraints", "Optimization challenges"]
          },
          resources: ["Whiteboard", "Problem scenario cards", "Worksheets", "Everyday objects"],
          crossCurricular: ["Mathematics (logical reasoning)", "Science (scientific method)"],
          homeworkTasks: [
            "Solve a household problem using 4 steps",
            "Document decision-making process",
            "Identify problems that could be solved with technology"
          ]
        },
        {
          id: "3.2",
          title: "Programming Concepts and Design",
          duration: "7 weeks",
          sessions: 6,
          overview: "Comprehensive programming unit covering design methodologies, algorithm representation, and coding fundamentals. Students progress from pseudocode to functional programs.",
          objectives: [
            "Apply Top-Down and Bottom-Up design approaches",
            "Create accurate pseudocode for algorithms",
            "Design flowcharts using standard symbols",
            "Understand variables, data types, and operators",
            "Implement modular programming with functions",
            "Demonstrate coding competency"
          ],
          prerequisites: ["Unit 3.1 completion", "Basic computer skills"],
          keyVocabulary: ["Pseudocode", "Flowchart", "Variable", "Function", "Module", "Debugging"],
          teachingStrategies: [
            "Incremental complexity",
            "Pair programming",
            "Code review sessions",
            "Project-based assessment"
          ],
          assessmentPlan: {
            formative: ["Pseudocode exercises", "Flowchart drawings", "Code checkpoints"],
            summative: [
              { title: "Pseudocode & Flowcharts", marks: 25, type: "Practical" },
              { title: "Programming Project", marks: 40, type: "Project" },
              { title: "Strand 3 Term Test", marks: 30, type: "Test" }
            ]
          },
          differentiation: {
            struggling: ["Code templates", "Guided exercises", "Visual debugging"],
            advanced: ["Complex algorithms", "Efficiency optimization", "Additional languages"]
          },
          resources: ["Computers", "Programming IDE", "Code examples", "Debugging tools"],
          crossCurricular: ["Mathematics (logic, operators)", "English (clear instructions)"],
          homeworkTasks: [
            "Write pseudocode for daily tasks",
            "Create flowcharts for processes",
            "Complete coding exercises"
          ]
        }
      ]
    },
    {
      id: "strand4",
      number: 4,
      title: "Microprocessor Programming",
      subtitle: "Embedded Systems & ICT Ethics",
      color: "red",
      units: [
        {
          id: "4.1",
          title: "Microprocessors and Embedded Systems",
          duration: "4 weeks",
          sessions: 6,
          overview: "Students explore microprocessor architecture, embedded systems, and their real-world applications. Focus on understanding how programmable microprocessors control devices in various industries.",
          objectives: [
            "Identify and explain microprocessor components",
            "Understand code translation (compilers, interpreters)",
            "Explain sensor and actuator interfacing",
            "Describe embedded systems and their importance",
            "Identify applications of programmable microprocessors"
          ],
          prerequisites: ["Programming fundamentals from Strand 3"],
          keyVocabulary: ["ALU", "Control Unit", "Register", "Bus", "Sensor", "Actuator", "Embedded"],
          teachingStrategies: [
            "Visual diagrams and animations",
            "Physical component demonstrations",
            "Real-world application case studies",
            "Simulation software"
          ],
          assessmentPlan: {
            formative: ["Component identification", "Diagram labeling", "Application analysis"],
            summative: [{ title: "Microprocessors Quiz", marks: 20, type: "Quiz" }]
          },
          differentiation: {
            struggling: ["Focus on main components", "Simplified diagrams", "Analogies"],
            advanced: ["Advanced architecture", "Programming embedded systems", "IoT projects"]
          },
          resources: ["Microprocessor diagrams", "Physical chips", "Sensors/actuators", "Simulation software"],
          crossCurricular: ["Physics (electronics)", "Science (environmental sensors)"],
          homeworkTasks: [
            "Identify embedded systems at home",
            "Research a specific microprocessor",
            "Design a simple embedded system concept"
          ]
        },
        {
          id: "4.2",
          title: "ICT Ethics and Social Issues",
          duration: "2 weeks",
          sessions: 4,
          overview: "Critical examination of ethical, social, environmental, and health issues related to ICT. Students analyze real-world scenarios and develop ethical decision-making frameworks.",
          objectives: [
            "Understand ethical concerns in ICT use",
            "Know relevant legislation and measures",
            "Analyze environmental impacts of ICT",
            "Understand e-waste management",
            "Recognize health issues from ICT use"
          ],
          prerequisites: ["All previous units", "Critical thinking skills"],
          keyVocabulary: ["Privacy", "Copyright", "Digital divide", "E-waste", "Ergonomics", "Cyberbullying"],
          teachingStrategies: [
            "Case study analysis",
            "Ethical debates and discussions",
            "Research and presentations",
            "Role-playing scenarios"
          ],
          assessmentPlan: {
            formative: ["Discussion participation", "Case analysis", "Reflection journals"],
            summative: [
              { title: "Ethics Case Study", marks: 20, type: "Project" },
              { title: "Final Examination", marks: 130, type: "Exam" }
            ]
          },
          differentiation: {
            struggling: ["Simplified case studies", "Guided analysis templates", "Focus on key issues"],
            advanced: ["Complex ethical dilemmas", "Policy recommendations", "Research papers"]
          },
          resources: ["Case studies", "Legislation documents", "Research materials", "Discussion prompts"],
          crossCurricular: ["Social Studies (society)", "Science (environment)", "Health (wellbeing)"],
          homeworkTasks: [
            "Research an ICT ethics case",
            "Interview about technology impacts",
            "Create awareness material"
          ]
        }
      ]
    }
  ]
};

// Get strand color classes
const getStrandColors = (color) => {
  const colors = {
    blue: { bg: 'bg-blue-500', light: 'bg-blue-50', border: 'border-blue-500', text: 'text-blue-700', gradient: 'from-blue-500 to-blue-600' },
    green: { bg: 'bg-green-500', light: 'bg-green-50', border: 'border-green-500', text: 'text-green-700', gradient: 'from-green-500 to-green-600' },
    purple: { bg: 'bg-purple-500', light: 'bg-purple-50', border: 'border-purple-500', text: 'text-purple-700', gradient: 'from-purple-500 to-purple-600' },
    red: { bg: 'bg-red-500', light: 'bg-red-50', border: 'border-red-500', text: 'text-red-700', gradient: 'from-red-500 to-red-600' }
  };
  return colors[color] || colors.blue;
};

export default function EmbeddedUnitPlans() {
  const [selectedStrand, setSelectedStrand] = useState('strand1');
  const [selectedUnit, setSelectedUnit] = useState(null);
  
  const data = unitPlansData;
  const currentStrand = data.strands.find(s => s.id === selectedStrand);
  const colors = getStrandColors(currentStrand?.color || 'blue');
  
  const totalUnits = data.strands.reduce((acc, s) => acc + s.units.length, 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className={`bg-gradient-to-r ${colors.gradient} text-white`}>
        <CardHeader>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <CardTitle className="text-2xl flex items-center gap-2">
                <FileText className="h-6 w-6" />
                Unit Plans - {data.subject}
              </CardTitle>
              <CardDescription className="text-white/80 mt-1">
                {data.strands.length} Strands • {totalUnits} Units • Detailed planning guides
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Button variant="secondary" size="sm">
                <Printer className="h-4 w-4 mr-2" />
                Print All
              </Button>
              <Button variant="secondary" size="sm">
                <Download className="h-4 w-4 mr-2" />
                Export
              </Button>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Strand Selector Tabs */}
      <Tabs value={selectedStrand} onValueChange={(v) => { setSelectedStrand(v); setSelectedUnit(null); }}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="strand1">Strand 1: ICT Systems</TabsTrigger>
          <TabsTrigger value="strand2">Strand 2: Web Dev</TabsTrigger>
          <TabsTrigger value="strand3">Strand 3: Programming</TabsTrigger>
          <TabsTrigger value="strand4">Strand 4: Ethics</TabsTrigger>
        </TabsList>

        {data.strands.map((strand) => {
          const strandColors = getStrandColors(strand.color);
          return (
            <TabsContent key={strand.id} value={strand.id} className="space-y-4 mt-4">
              {/* Strand Header */}
              <Card className={`border-l-4 ${strandColors.border} ${strandColors.light}`}>
                <CardHeader>
                  <div className="flex items-center gap-2 mb-2">
                    <Badge className={strandColors.bg}>Strand {strand.number}</Badge>
                  </div>
                  <CardTitle>{strand.title}</CardTitle>
                  <CardDescription>{strand.subtitle} • {strand.units.length} Units</CardDescription>
                </CardHeader>
              </Card>

              {/* Unit Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {strand.units.map((unit) => (
                  <Card 
                    key={unit.id}
                    className={`cursor-pointer transition-all hover:shadow-lg ${selectedUnit === unit.id ? 'ring-2 ring-primary' : ''}`}
                    onClick={() => setSelectedUnit(selectedUnit === unit.id ? null : unit.id)}
                  >
                    <CardHeader className={`pb-2 bg-gradient-to-r ${strandColors.gradient} text-white rounded-t-lg`}>
                      <div className="flex items-center justify-between">
                        <Badge variant="secondary">Unit {unit.id}</Badge>
                        <div className="flex items-center gap-1 text-xs">
                          <Clock className="h-3 w-3" />
                          {unit.duration}
                        </div>
                      </div>
                      <CardTitle className="text-base mt-2">{unit.title}</CardTitle>
                    </CardHeader>
                    <CardContent className="pt-4">
                      <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
                        {unit.overview}
                      </p>
                      <div className="flex items-center justify-between text-xs text-muted-foreground">
                        <span>{unit.sessions} Sessions</span>
                        <span>{unit.objectives.length} Objectives</span>
                      </div>
                      <div className="flex gap-1 mt-2 flex-wrap">
                        {unit.assessmentPlan.summative.map((a, i) => (
                          <Badge key={i} variant="outline" className="text-xs">
                            {a.type}: {a.marks}m
                          </Badge>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Selected Unit Detail */}
              {selectedUnit && (
                <Card className="mt-6">
                  {(() => {
                    const unit = strand.units.find(u => u.id === selectedUnit);
                    if (!unit) return null;
                    return (
                      <>
                        <CardHeader className={`bg-gradient-to-r ${strandColors.gradient} text-white`}>
                          <div className="flex items-center justify-between">
                            <div>
                              <Badge variant="secondary" className="mb-2">Unit {unit.id}</Badge>
                              <CardTitle className="text-xl">{unit.title}</CardTitle>
                              <CardDescription className="text-white/80">
                                {unit.duration} • {unit.sessions} Sessions
                              </CardDescription>
                            </div>
                            <div className="flex gap-2">
                              <Button variant="secondary" size="sm">
                                <Download className="h-4 w-4 mr-1" />
                                Download
                              </Button>
                              <Button variant="secondary" size="sm">
                                <Printer className="h-4 w-4 mr-1" />
                                Print
                              </Button>
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent className="pt-6 space-y-6">
                          {/* Overview */}
                          <div>
                            <h4 className="font-semibold flex items-center gap-2 mb-2">
                              <BookOpen className={`h-4 w-4 ${strandColors.text}`} />
                              Unit Overview
                            </h4>
                            <p className="text-muted-foreground">{unit.overview}</p>
                          </div>

                          <Separator />

                          {/* Learning Objectives */}
                          <div>
                            <h4 className="font-semibold flex items-center gap-2 mb-3">
                              <Target className={`h-4 w-4 ${strandColors.text}`} />
                              Learning Objectives
                            </h4>
                            <ul className="space-y-2">
                              {unit.objectives.map((obj, i) => (
                                <li key={i} className="flex items-start gap-2">
                                  <CheckCircle2 className="h-4 w-4 text-green-500 mt-1 flex-shrink-0" />
                                  <span className="text-sm">{obj}</span>
                                </li>
                              ))}
                            </ul>
                          </div>

                          <Separator />

                          {/* Prerequisites & Key Vocabulary */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                              <h4 className="font-semibold flex items-center gap-2 mb-2">
                                <LinkIcon className="h-4 w-4 text-gray-600" />
                                Prerequisites
                              </h4>
                              <ul className="space-y-1">
                                {unit.prerequisites.map((pre, i) => (
                                  <li key={i} className="text-sm text-muted-foreground flex items-center gap-2">
                                    <ChevronRight className="h-3 w-3" />
                                    {pre}
                                  </li>
                                ))}
                              </ul>
                            </div>
                            <div>
                              <h4 className="font-semibold flex items-center gap-2 mb-2">
                                <BookOpen className="h-4 w-4 text-gray-600" />
                                Key Vocabulary
                              </h4>
                              <div className="flex flex-wrap gap-1">
                                {unit.keyVocabulary.map((word, i) => (
                                  <Badge key={i} variant="outline" className="text-xs">{word}</Badge>
                                ))}
                              </div>
                            </div>
                          </div>

                          <Separator />

                          {/* Teaching Strategies */}
                          <div>
                            <h4 className="font-semibold flex items-center gap-2 mb-3">
                              <Lightbulb className={`h-4 w-4 ${strandColors.text}`} />
                              Teaching Strategies
                            </h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                              {unit.teachingStrategies.map((strategy, i) => (
                                <div key={i} className="flex items-center gap-2 p-2 bg-muted/50 rounded">
                                  <Activity className="h-4 w-4 text-muted-foreground" />
                                  <span className="text-sm">{strategy}</span>
                                </div>
                              ))}
                            </div>
                          </div>

                          <Separator />

                          {/* Assessment Plan */}
                          <div>
                            <h4 className="font-semibold flex items-center gap-2 mb-3">
                              <Award className={`h-4 w-4 ${strandColors.text}`} />
                              Assessment Plan
                            </h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div className="p-4 bg-gray-50 rounded-lg">
                                <h5 className="font-medium text-sm mb-2">Formative Assessment</h5>
                                <ul className="space-y-1">
                                  {unit.assessmentPlan.formative.map((item, i) => (
                                    <li key={i} className="text-sm text-muted-foreground flex items-center gap-2">
                                      <CheckCircle2 className="h-3 w-3 text-blue-500" />
                                      {item}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                              <div className={`p-4 ${strandColors.light} rounded-lg`}>
                                <h5 className="font-medium text-sm mb-2">Summative Assessment</h5>
                                {unit.assessmentPlan.summative.map((item, i) => (
                                  <div key={i} className="flex items-center justify-between p-2 bg-white rounded mb-1">
                                    <div>
                                      <p className="text-sm font-medium">{item.title}</p>
                                      <Badge variant="outline" className="text-xs">{item.type}</Badge>
                                    </div>
                                    <p className="text-lg font-bold text-primary">{item.marks}m</p>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>

                          <Separator />

                          {/* Differentiation */}
                          <div>
                            <h4 className="font-semibold flex items-center gap-2 mb-3">
                              <Users className={`h-4 w-4 ${strandColors.text}`} />
                              Differentiation Strategies
                            </h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                                <h5 className="font-medium text-sm text-amber-800 mb-2">For Struggling Learners</h5>
                                <ul className="space-y-1">
                                  {unit.differentiation.struggling.map((item, i) => (
                                    <li key={i} className="text-sm text-amber-700 flex items-center gap-2">
                                      <ChevronRight className="h-3 w-3" />
                                      {item}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                                <h5 className="font-medium text-sm text-green-800 mb-2">For Advanced Learners</h5>
                                <ul className="space-y-1">
                                  {unit.differentiation.advanced.map((item, i) => (
                                    <li key={i} className="text-sm text-green-700 flex items-center gap-2">
                                      <ChevronRight className="h-3 w-3" />
                                      {item}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            </div>
                          </div>

                          <Separator />

                          {/* Resources & Cross-Curricular */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                              <h4 className="font-semibold flex items-center gap-2 mb-2">
                                <ClipboardList className="h-4 w-4 text-gray-600" />
                                Resources Required
                              </h4>
                              <div className="flex flex-wrap gap-1">
                                {unit.resources.map((resource, i) => (
                                  <Badge key={i} variant="secondary" className="text-xs">{resource}</Badge>
                                ))}
                              </div>
                            </div>
                            <div>
                              <h4 className="font-semibold flex items-center gap-2 mb-2">
                                <LinkIcon className="h-4 w-4 text-gray-600" />
                                Cross-Curricular Links
                              </h4>
                              <div className="flex flex-wrap gap-1">
                                {unit.crossCurricular.map((link, i) => (
                                  <Badge key={i} variant="outline" className="text-xs">{link}</Badge>
                                ))}
                              </div>
                            </div>
                          </div>

                          <Separator />

                          {/* Homework Tasks */}
                          <div>
                            <h4 className="font-semibold flex items-center gap-2 mb-3">
                              <GraduationCap className={`h-4 w-4 ${strandColors.text}`} />
                              Homework Tasks
                            </h4>
                            <ul className="space-y-2">
                              {unit.homeworkTasks.map((task, i) => (
                                <li key={i} className="flex items-start gap-2 p-2 bg-muted/30 rounded">
                                  <span className="font-bold text-muted-foreground">{i + 1}.</span>
                                  <span className="text-sm">{task}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </CardContent>
                      </>
                    );
                  })()}
                </Card>
              )}
            </TabsContent>
          );
        })}
      </Tabs>
    </div>
  );
}
